import usePrivateAxios from "@/common/api/usePrivateAxios";
import { defaultTransformerResponse } from "@/common/constants/defaultTransformerResponse.constant";
import { MetaResponse } from "@/common/types/metaResponse";
import { RewardTicketData } from "@/common/types/rewardTickets.type";
import { InfiniteData, keepPreviousData, useInfiniteQuery, UseInfiniteQueryOptions } from "@tanstack/react-query";

type UseRewardTicketsInfiniteProps<T> = {
  id?: string
  filters?: {
    winPositionNotNull?: boolean
  },
  options?: Partial<UseInfiniteQueryOptions<any, any, InfiniteData<any>>>
  mapper?: (data: any) => T
}

export const useRewardTicketsInfinite = <T,>({
  filters: { winPositionNotNull } = {},
  id,
  options,
  mapper,
}: UseRewardTicketsInfiniteProps<T>) => {
  const privateAxios = usePrivateAxios();

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    ...rest
  } = useInfiniteQuery({
    queryKey: ['rewardTickets', winPositionNotNull],
    queryFn: ({pageParam}) => {
      return privateAxios.get<{ data: RewardTicketData[] }>(
        `/tmais/raffles/${id}/reward_tickets`,
        {
          params: { 
            'q[win_position_not_null]': winPositionNotNull?.toString(),
            page: pageParam,
          },
        }
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: { data: { data: any[]; meta: MetaResponse } }) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1
      }
    },
    placeholderData: keepPreviousData,
    ...options,
    select: (data) => {
      const flatData = data?.pages?.reduce((acc, page) => {
        return [...acc, ...page?.data?.data]
      }, [] as RewardTicketData[])

      return {
        data: mapper ? mapper(flatData) : (flatData as T),
        meta: data?.pages[data?.pages?.length - 1]?.data?.meta as MetaResponse,
      }
    },
  });

  return { data, meta, ...rest };
};
